/**
 * Premium cinematic "Sign-Off" outro.
 *
 * Visual design:
 *  - Letterbox bands collapse in (top + bottom) to lock the frame into a film
 *    aspect — instant cinematic shift.
 *  - Centred: brand logo (watermark), a thin underline of accent colour, then
 *    headline ("Werde auch DU Partner"), subtext, and a glowing CTA pill with
 *    the URL.
 *  - Background runs a slow sweep of accent-tinted blobs and faint film grain
 *    for premium texture.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, makeTl,
  resolveBrandLogoSrc, typeSize, weights, withAlpha, colors as themeColors,
} from "../../reel/sceneShared.js";

export const signOff: SceneBuilder = ({
  ctx, clipPrefix, inputs, clipDurationSec, clipStartSec,
}) => {
  const accent = accentFor(inputs);
  const isVertical = ctx.format === "9:16";
  const headline = inputs.text ?? "Werde auch DU Partner";
  const sub = inputs.subtext ?? "";
  const cta = inputs.number ?? "Jetzt registrieren";
  const url = inputs.url ?? "";
  // Wichtig: `extras.hideLogo` wird vom Reel-Renderer für das per-Clip
  // Watermark verwendet — wir dürfen es hier NICHT zusätzlich für das
  // interne Sign-Off-Logo interpretieren, sonst doppelten sich Effekte.
  // Das zentrale Logo gehört zum Cinematic-Outro-Design und wird immer
  // gerendert (kann optional via `extras.hideSceneLogo` ausgeblendet
  // werden, z. B. wenn der User stattdessen die freie Watermark nutzen
  // möchte).
  const hideSceneLogo = inputs.extras?.hideSceneLogo === true;
  const scale = typeof inputs.extras?.scale === "number" ? Number(inputs.extras.scale) : 1;
  const logoSrc = resolveBrandLogoSrc();

  const headlineSize = typeSize(isVertical ? "hero" : "mega", ctx.format) * 0.78 * scale;
  const subSize = typeSize("md", ctx.format) * 0.78;
  const ctaSize = typeSize("md", ctx.format) * 0.7;
  const urlSize = typeSize("sm", ctx.format) * 0.8;
  const logoH = isVertical ? 120 : 96;

  const t = makeTl(clipStartSec);
  const logoId = `${clipPrefix}-logo`;
  const underlineId = `${clipPrefix}-ul`;
  const headlineId = `${clipPrefix}-h`;
  const subId = `${clipPrefix}-sub`;
  const ctaWrapId = `${clipPrefix}-cta-wrap`;
  const ctaId = `${clipPrefix}-cta`;
  const ctaGlowId = `${clipPrefix}-cta-glow`;
  const urlId = `${clipPrefix}-url`;

  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "cinematic", accent });

  const logoBlock = !hideSceneLogo && logoSrc
    ? `<div id="${logoId}" style="display:flex;align-items:center;gap:18px;opacity:0;">
        <img src="${escapeHtml(logoSrc)}" alt="brand" style="height:${logoH}px;width:auto;object-fit:contain;" />
      </div>`
    : "";

  const padV = isVertical ? 120 : 80;
  const padH = isVertical ? 120 : 80;

  const html = `
<div id="${clipPrefix}-pad" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:${isVertical ? 32 : 24}px;padding:${padV}px ${padH}px;">
  ${logoBlock}
  <div id="${underlineId}" style="height:2px;width:0;background:${accent};box-shadow:0 0 14px ${withAlpha(accent, 0.7)};"></div>

  <div id="${headlineId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${headlineSize}px;line-height:1.02;letter-spacing:-2.5px;color:${themeColors.fg};text-align:center;text-wrap:balance;max-width:${isVertical ? 880 : 1400}px;text-shadow:0 0 50px ${withAlpha(accent, 0.45)};">${escapeHtml(headline)}</div>

  ${sub ? `<div id="${subId}" style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${subSize * 0.7}px;letter-spacing:0.3px;line-height:1.4;color:${withAlpha(themeColors.fg, 0.72)};text-align:center;max-width:${isVertical ? 760 : 1100}px;">${escapeHtml(sub)}</div>` : ""}

  <div id="${ctaWrapId}" style="position:relative;display:inline-flex;flex-direction:column;align-items:center;gap:18px;margin-top:${isVertical ? 18 : 8}px;">
    <div id="${ctaGlowId}" style="position:absolute;left:50%;top:50%;width:140%;height:200%;transform:translate(-50%,-50%);background:radial-gradient(50% 50% at 50% 50%, ${withAlpha(accent, 0.55)} 0%, transparent 70%);filter:blur(8px);pointer-events:none;opacity:0;"></div>
    <div id="${ctaId}" style="position:relative;display:inline-flex;align-items:center;gap:18px;padding:${isVertical ? 28 : 22}px ${isVertical ? 56 : 44}px;border-radius:9999px;background:linear-gradient(180deg, ${accent} 0%, ${withAlpha(accent, 0.88)} 100%);box-shadow:0 18px 50px ${withAlpha(accent, 0.55)}, inset 0 1px 0 ${withAlpha(themeColors.fg, 0.45)};font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${ctaSize}px;letter-spacing:1px;text-transform:uppercase;color:${themeColors.bg};">
      <span>${escapeHtml(cta)}</span>
      <svg width="${Math.round(ctaSize * 1.05)}" height="${Math.round(ctaSize * 1.05)}" viewBox="0 0 24 24" fill="none" stroke="${themeColors.bg}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
    </div>
    ${url ? `<div id="${urlId}" style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${urlSize}px;letter-spacing:2px;text-transform:uppercase;color:${withAlpha(themeColors.fg, 0.78)};">${escapeHtml(url)}</div>` : ""}
  </div>
</div>`;

  const init = [
    logoBlock ? `gsap.set("#${logoId}", { opacity: 0, y: -10 });` : "",
    `gsap.set("#${underlineId}", { width: 0 });`,
    `gsap.set("#${headlineId}", { opacity: 0, y: 24 });`,
    sub ? `gsap.set("#${subId}", { opacity: 0, y: 16 });` : "",
    `gsap.set("#${ctaWrapId}", { opacity: 0, y: 24 });`,
    `gsap.set("#${ctaGlowId}", { opacity: 0 });`,
  ].filter(Boolean).join("\n");

  const tweens: string[] = [];
  if (logoBlock) tweens.push(t.to(`#${logoId}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power2.out\"" }, 0.35));
  tweens.push(t.to(`#${underlineId}`, { width: 64, duration: 0.4, ease: "raw:\"power2.out\"" }, 0.7));
  tweens.push(t.to(`#${headlineId}`, { opacity: 1, y: 0, duration: 0.6, ease: "raw:\"power3.out\"" }, 0.85));
  if (sub) tweens.push(t.to(`#${subId}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power2.out\"" }, 1.1));
  // CTA pop with glow.
  tweens.push(t.to(`#${ctaWrapId}`, { opacity: 1, y: 0, duration: 0.55, ease: "raw:\"back.out(1.6)\"" }, 1.3));
  tweens.push(t.fromTo(`#${ctaGlowId}`, { opacity: 0, scale: 0.6 }, { opacity: 1, scale: 1, duration: 0.55, ease: "raw:\"power2.out\"" }, 1.35));
  // Subtle CTA breathing.
  const dwell = Math.max(0.6, clipDurationSec - 2.0);
  tweens.push(t.to(`#${ctaGlowId}`, { opacity: 0.65, duration: dwell, ease: "raw:\"sine.inOut\"" }, 1.9));
  tweens.push(t.to(`#${ctaId}`, { scale: 1.04, duration: dwell, ease: "raw:\"sine.inOut\"" }, 1.9));

  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec, 10));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
